/**
 * Funções utilitárias para o sistema de horários
 */
import { FERIADOS_PT } from '../constants/horarioConstants';

/**
 * Obtém todos os dias entre duas datas
 * CORRIGIDO: Usa cálculo baseado em componentes de data para evitar problemas com DST
 */
export const getDaysInPeriod = (inicio, fim) => {
  const days = [];
  
  // Extrair componentes da data de início
  const [startYear, startMonth, startDay] = inicio.split('-').map(Number);
  const [endYear, endMonth, endDay] = fim.split('-').map(Number);
  
  // Criar data de início e fim usando UTC para evitar problemas de DST
  let currentDate = new Date(Date.UTC(startYear, startMonth - 1, startDay));
  const endDate = new Date(Date.UTC(endYear, endMonth - 1, endDay));
  
  // Iterar por cada dia
  while (currentDate <= endDate) {
    // Formatar como YYYY-MM-DD usando componentes UTC
    const year = currentDate.getUTCFullYear();
    const month = String(currentDate.getUTCMonth() + 1).padStart(2, '0');
    const day = String(currentDate.getUTCDate()).padStart(2, '0');
    days.push(`${year}-${month}-${day}`);
    
    // Avançar um dia em UTC
    currentDate.setUTCDate(currentDate.getUTCDate() + 1);
  }
  
  return days;
};

/**
 * Verifica se uma data é fim de semana
 */
export const isWeekend = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  const day = date.getDay();
  return day === 0 || day === 6;
};

/**
 * Verifica se uma data é feriado
 */
export const isFeriado = (dateStr) => {
  const year = new Date(dateStr + 'T00:00:00').getFullYear();
  const feriadosDoAno = FERIADOS_PT[year] || [];
  return feriadosDoAno.includes(dateStr);
};

/**
 * Calcula o número de semanas entre duas datas
 */
export const calcularSemanas = (inicio, fim) => {
  if (!inicio || !fim) return 0;
  const startDate = new Date(inicio);
  const endDate = new Date(fim);
  const diasTotais = Math.floor((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  return diasTotais / 7;
};

/**
 * Calcula o número de dias úteis (seg-sex, excluindo feriados) entre duas datas
 * NOTA: Esta função existe mas NÃO é usada no cálculo de horas esperadas
 */
export const calcularDiasUteis = (inicio, fim) => {
  if (!inicio || !fim) return 0;
  
  const days = getDaysInPeriod(inicio, fim);
  let diasUteis = 0;
  
  for (const day of days) {
    if (!isWeekend(day) && !isFeriado(day)) {
      diasUteis++;
    }
  }
  
  return diasUteis;
};

/**
 * Arredonda horas para múltiplos de 15 minutos (0.25h)
 * Exemplos:
 * - 6.63h → 6.75h (6h45m)
 * - -202.63h → -202.75h (-202h45m)
 */
export const arredondar15min = (horas) => {
  return Math.round(horas * 4) / 4;
};

/**
 * Calcula as horas esperadas para um período
 * REGRA: Semanas × 40h (40 horas por semana de trabalho)
 * 
 * Exemplo: Dezembro 2025 (31 dias)
 * - 31 dias ÷ 7 = 4.43 semanas
 * - 4.43 × 40h = 177.14h esperadas
 */
export const calcularHorasEsperadas = (inicio, fim) => {
  const semanas = calcularSemanas(inicio, fim);
  return semanas * 40; // 40 horas por semana
};

/**
 * Obtém o nome do dia da semana
 */
export const getDiaSemana = (dateStr) => {
  const diasSemana = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
  const date = new Date(dateStr + 'T00:00:00');
  return diasSemana[date.getDay()];
};

/**
 * Formata uma data para exibição
 */
export const formatarDataExibicao = (dateStr) => {
  const date = new Date(dateStr + 'T00:00:00');
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
};

/**
 * Valida se um horário está no formato correto (HH:MM)
 */
export const validarFormatoHorario = (horario) => {
  const regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return regex.test(horario);
};

/**
 * Calcula horas entre dois horários
 */
export const calcularHorasEntre = (entrada, saida) => {
  if (!entrada || !saida) return 0;
  
  const [hEntrada, mEntrada] = entrada.split(':').map(Number);
  const [hSaida, mSaida] = saida.split(':').map(Number);
  
  let totalMinutos = (hSaida * 60 + mSaida) - (hEntrada * 60 + mEntrada);
  
  // Se saída é menor que entrada, assume que passou para o próximo dia
  if (totalMinutos < 0) {
    totalMinutos += 24 * 60;
  }
  
  return totalMinutos / 60;
};

/**
 * Parseia uma string de turno para extrair entrada e saída
 */
export const parsearTurno = (turnoStr) => {
  if (!turnoStr) return null;
  
  // Remove "APOIO" do início se existir
  const cleanStr = turnoStr.replace(/^APOIO\s+/i, '').trim();
  
  // Tenta match com formato HH:MM-HH:MM
  const match = cleanStr.match(/(\d{1,2}):?(\d{2})\s*-\s*(\d{1,2}):?(\d{2})/);
  
  if (match) {
    const [, h1, m1, h2, m2] = match;
    return {
      entrada: `${h1.padStart(2, '0')}:${m1}`,
      saida: `${h2.padStart(2, '0')}:${m2}`
    };
  }
  
  return null;
};
